/**
 * OrionMemoryManagement — Admin
 * Gestion de la mémoire persistante de l'agent Orion.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Brain, Plus, Trash2, Loader, Save } from 'lucide-react';
import { getHRAgentMemory, addHRAgentMemory, deleteHRAgentMemory } from '../../api';

const MEMORY_TYPES = [
  { value: 'learning',   label: 'Apprentissage', color: 'bg-blue-100 text-blue-700' },
  { value: 'context',    label: 'Contexte RH',   color: 'bg-purple-100 text-purple-700' },
  { value: 'preference', label: 'Préférence',    color: 'bg-green-100 text-green-700' },
  { value: 'error',      label: 'Erreur',        color: 'bg-red-100 text-red-700' },
];

const EMPTY_FORM = { type: 'learning', content: '', source: 'manual' };

const OrionMemoryManagement = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getHRAgentMemory();
      setEntries(res.data || []);
    } catch {
      setError('Erreur de chargement');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.content.trim()) return;
    setAdding(true);
    setError(null);
    try {
      await addHRAgentMemory({
        type: form.type,
        content: form.content.trim(),
        source: form.source.trim() || 'manual',
      });
      setForm(EMPTY_FORM);
      await load();
    } catch {
      setError('Erreur lors de l\'ajout');
    }
    setAdding(false);
  };

  const handleDelete = async (id) => {
    try {
      await deleteHRAgentMemory(id);
      setEntries(prev => prev.filter(e => e.id !== id));
    } catch {
      setError('Erreur lors de la suppression');
    }
  };

  const typeInfo = (type) => MEMORY_TYPES.find(t => t.value === type) || { label: type, color: 'bg-gray-100 text-gray-600' };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader className="animate-spin text-primary" size={24} /></div>;
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
      )}

      {/* Formulaire d'ajout */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Plus size={16} className="text-primary" />
            Ajouter une entrée mémoire Orion
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-primary/5 rounded-lg px-4 py-3 text-sm text-gray-700 border border-primary/20 mb-4">
            La mémoire est injectée dans chaque conversation d'Orion. Utilisez-la pour des règles RH spécifiques à votre structure, des erreurs connues à éviter, ou des préférences de réponse.
          </div>
          <form onSubmit={handleAdd} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={form.type}
                  onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
                  {MEMORY_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                <input
                  type="text"
                  value={form.source}
                  onChange={e => setForm(f => ({ ...f, source: e.target.value }))}
                  placeholder="manual"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contenu</label>
              <textarea
                rows={3}
                value={form.content}
                onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                placeholder="Ex: Notre structure emploie 8 personnes. La directrice est Mme Martin. Ne jamais citer de montants sans vérifier le tableau des salaires."
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>
            <div className="flex justify-end">
              <button type="submit" disabled={adding || !form.content.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark disabled:opacity-50 transition">
                {adding ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
                {adding ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Liste des entrées */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Brain size={16} className="text-primary" />
            Mémoire active ({entries.length} entrée{entries.length !== 1 ? 's' : ''})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {entries.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Brain size={32} className="mx-auto mb-3 text-gray-300" />
              <p className="text-sm">Aucune entrée en mémoire.</p>
              <p className="text-xs mt-1">Ajoutez des informations persistantes qu'Orion doit toujours connaître.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {entries.map(entry => {
                const t = typeInfo(entry.type);
                return (
                  <div key={entry.id} className="flex items-start gap-3 py-3 group">
                    <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium mt-0.5 ${t.color}`}>
                      {t.label}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 leading-relaxed">{entry.content}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {entry.source && `Source : ${entry.source} — `}
                        {new Date(entry.createdAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDelete(entry.id)}
                      className="flex-shrink-0 opacity-0 group-hover:opacity-100 p-1.5 text-gray-300 hover:text-red-500 rounded-lg transition">
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OrionMemoryManagement;
